import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Session timeout configuration (in milliseconds)
 */
const SESSION_CONFIG = {
  // Warn user 2 minutes before timeout
  WARNING_BEFORE_TIMEOUT_MS: 2 * 60 * 1000,
  // Default session timeout: 30 minutes of inactivity
  DEFAULT_TIMEOUT_MS: 30 * 60 * 1000,
  // Events that reset the timeout
  ACTIVITY_EVENTS: ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'],
  // Debounce activity updates
  DEBOUNCE_MS: 1000,
};

/**
 * Hook to manage session timeout with activity tracking
 * 
 * @param {Object} options Configuration options
 * @param {number} options.timeoutMs - Session timeout in milliseconds
 * @param {number} options.warningMs - Time before timeout to show warning
 * @param {Function} options.onTimeout - Callback when session times out
 * @param {Function} options.onWarning - Callback when warning should show
 * @param {boolean} options.enabled - Whether timeout is enabled
 */
export const useSessionTimeout = ({
  timeoutMs = SESSION_CONFIG.DEFAULT_TIMEOUT_MS,
  warningMs = SESSION_CONFIG.WARNING_BEFORE_TIMEOUT_MS,
  onTimeout,
  onWarning,
  enabled = true,
}) => {
  const [lastActivity, setLastActivity] = useState(() => Date.now());
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(timeoutMs);
  
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const intervalRef = useRef(null);
  const debounceRef = useRef(null);

  // Reset activity timestamp (debounced)
  const resetActivity = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      setLastActivity(Date.now());
      setShowWarning(false);
      
      // Store last activity in localStorage for cross-tab sync
      localStorage.setItem('lastActivity', Date.now().toString());
    }, SESSION_CONFIG.DEBOUNCE_MS);
  }, []);

  // Handle activity events
  useEffect(() => {
    if (!enabled) return;

    const handleActivity = () => {
      resetActivity();
    };

    // Listen for activity events
    SESSION_CONFIG.ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Listen for storage events (cross-tab activity sync)
    const handleStorageChange = (e) => {
      if (e.key === 'lastActivity') {
        const newActivity = parseInt(e.newValue, 10);
        if (newActivity > lastActivity) {
          setLastActivity(newActivity);
          setShowWarning(false);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      SESSION_CONFIG.ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('storage', handleStorageChange);
      
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [enabled, resetActivity, lastActivity]);

  // Timer logic
  useEffect(() => {
    if (!enabled) return;

    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);

    const now = Date.now();
    const timeSinceActivity = now - lastActivity;
    const timeUntilTimeout = timeoutMs - timeSinceActivity;
    const timeUntilWarning = timeUntilTimeout - warningMs;

    // Update remaining time every second when warning is shown
    if (showWarning) {
      intervalRef.current = setInterval(() => {
        const remaining = timeoutMs - (Date.now() - lastActivity);
        setRemainingTime(Math.max(0, remaining));
      }, 1000);
    }

    // Set warning timer
    if (timeUntilWarning > 0) {
      warningRef.current = setTimeout(() => {
        setShowWarning(true);
        if (onWarning) onWarning();
      }, timeUntilWarning);
    } else if (timeUntilTimeout > 0 && timeUntilWarning <= 0) {
      // Already in warning period
      setShowWarning(true);
      if (onWarning) onWarning();
    }

    // Set timeout timer
    if (timeUntilTimeout > 0) {
      timeoutRef.current = setTimeout(() => {
        setShowWarning(false);
        if (onTimeout) onTimeout();
      }, timeUntilTimeout);
    } else {
      // Already timed out
      if (onTimeout) onTimeout();
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enabled, lastActivity, timeoutMs, warningMs, showWarning, onTimeout, onWarning]);

  // Extend session (user clicks "Stay logged in")
  const extendSession = useCallback(() => {
    resetActivity();
  }, [resetActivity]);

  // Format remaining time for display
  const formatRemainingTime = useCallback(() => {
    const seconds = Math.ceil(remainingTime / 1000);
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }, [remainingTime]);

  return {
    showWarning,
    remainingTime,
    formatRemainingTime,
    extendSession,
    resetActivity,
    lastActivity,
  };
};

export default useSessionTimeout;
